// The click-by-click tools' drafts — a wall, a line, a polygon, a room's corners,
// a floor, a revision cloud, a leader, a repeat run, a bench outline, a channel, a
// starry sky's zone, a connection's start and bends; on a sheet, the paper Line
// and Polygon. Each tool's rule (`POLYLINE_RULES`) says what it takes to be
// finished and whether it closes, and every way of ending or growing a draft goes
// through here, so no two of them can disagree:
//  - Enter, a right-click, a double-click and Esc finish it (`finishDraft`) once
//    it has its points, and Esc otherwise drops it (`dropDraft`);
//  - the typed options act on it: Close (`closeDraft`) and Undo (`undoPoint`);
//  - a point, clicked or typed, goes through `addPoint`: one on the first point
//    closes the draft, one on the last finishes a leader, one the tool refuses
//    (a room's corner too close to another) is not added.
// The points themselves live in main.ts, one array per tool, read and written by
// its snapping, hover and preview: this module rules on them. DOM-free.

import type { Vec2 } from '../shared/types';
import {
  CLOSE_OPTION,
  type ToolOption,
  UNDO_OPTION,
} from './commands';

export type PolylineTool =
  | 'bench-poly'
  | 'connection'
  | 'floor-screed'
  | 'floor-screed-slope'
  | 'floor-tiled'
  | 'floor-tiled-slope'
  | 'leader'
  | 'light-channel'
  | 'light-sky'
  | 'line'
  | 'poly-room'
  | 'polygon'
  | 'repeat'
  | 'revision-cloud'
  | 'sheet-line'
  | 'sheet-polygon'
  | 'wall';

export interface PolylineRule {
  /** Points it needs to be finished. */
  min: number;
  /** How it closes — the Close option, or a click on its first point: `shape`,
   *  its points are a closed outline as they stand (a polygon); `ring`, the first
   *  point is repeated (a wall or a line run back to its start). None: it doesn't. */
  close?: 'shape' | 'ring';
  /** Points before Close is offered (3 unless said). */
  closeMin?: number;
  /** A point on its last point finishes it (a leader: then its label; a
   *  connection: on that point, its last bend). */
  lastFinishes?: boolean;
}

export const POLYLINE_RULES: Record<PolylineTool, PolylineRule> = {
  'bench-poly': { min: 2 },
  // Its start (a pick: an object, a point of one, or a free point), then its
  // bends; a click on an object ends it there (main.ts), Enter on its last bend.
  connection: { min: 2, lastFinishes: true },
  'floor-screed': { min: 3, close: 'shape' },
  'floor-screed-slope': { min: 3, close: 'shape' },
  'floor-tiled': { min: 3, close: 'shape' },
  'floor-tiled-slope': { min: 3, close: 'shape' },
  leader: { min: 2, lastFinishes: true },
  'light-channel': { min: 2 },
  'light-sky': { min: 3, close: 'shape' },
  line: { min: 2, close: 'ring', closeMin: 2 },
  'poly-room': { min: 3, close: 'shape' },
  polygon: { min: 3, close: 'shape' },
  repeat: { min: 2 },
  'revision-cloud': { min: 3, close: 'shape' },
  'sheet-line': { min: 2, close: 'ring', closeMin: 2 },
  'sheet-polygon': { min: 3, close: 'shape' },
  wall: { min: 2, close: 'ring' },
};

export const isPolylineTool = (t: string): t is PolylineTool => t in POLYLINE_RULES;

/** A tool's draft as its owner holds it. */
export interface PolylineDraft {
  /** The points, the live array (a point is pushed or popped in place). */
  pts: Vec2[];
  /** The tool's finish: makes what was drawn and clears the draft. */
  finish(): void;
  /** Drops the draft. */
  clear(): void;
  /** Why the tool will not take this point (a corner too close…), else null. */
  refuse?(p: Vec2): string | null;
}

/** Finish it if it has its points — Enter, a right-click, a double-click, Esc. */
export function finishDraft(rule: PolylineRule, d: PolylineDraft): boolean {
  if (d.pts.length < rule.min) return false;
  d.finish();
  return true;
}

/** Drop it if it has a point — Esc, when it could not be finished. */
export function dropDraft(d: PolylineDraft): boolean {
  if (!d.pts.length) return false;
  d.clear();
  return true;
}

/** The typed options it takes now: Close once it can close, and Undo. */
export function draftOptions(rule: PolylineRule, pts: readonly Vec2[]): ToolOption[] {
  if (!pts.length) return [];
  return rule.close && pts.length >= (rule.closeMin ?? 3) ? [CLOSE_OPTION, UNDO_OPTION] : [UNDO_OPTION];
}

/** Close it — the Close option. */
export function closeDraft(rule: PolylineRule, d: PolylineDraft): boolean {
  if (!rule.close || d.pts.length < (rule.closeMin ?? 3)) return false;
  if (rule.close === 'ring') d.pts.push({ ...d.pts[0] });
  d.finish();
  return true;
}

/** Take its last point back — the Undo option. */
export function undoPoint(d: PolylineDraft): boolean {
  return d.pts.pop() != null;
}

export type AddResult = { added: true } | { finished: true } | { refused: string };

/** A point for the draft, clicked or typed. Within `tol` (drawing units: a click's
 *  pick radius, 0 for a typed point) of its first point it closes a draft of 3 or
 *  more that closes; of its last, it finishes one whose last point finishes it;
 *  a point the tool refuses is not added, and says why. */
export function addPoint(rule: PolylineRule, d: PolylineDraft, p: Vec2, tol: number): AddResult {
  const n = d.pts.length;
  const near = (q: Vec2): boolean => Math.hypot(p.x - q.x, p.y - q.y) <= tol;
  if (rule.close && n >= 3 && near(d.pts[0])) {
    if (rule.close === 'ring') d.pts.push({ ...d.pts[0] });
    d.finish();
    return { finished: true };
  }
  if (rule.lastFinishes && n >= rule.min && near(d.pts[n - 1])) {
    d.finish();
    return { finished: true };
  }
  const why = d.refuse?.(p);
  if (why) return { refused: why };
  d.pts.push(p);
  return { added: true };
}
